import appStore from 'client/AppStore';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {setUpStore} from 'client/framework';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {getSubmissionStatus} from './AssignmentResultsStudentsList/assignmentResultsStudentsList.utils';
import {stats, getStatusNameByAccuracy} from 'lib/StatsUtil';
import BlockyDropdownStore from 'generic/BlockyDropdown/BlockyDropdownStore';

export default {
  // We need to make this query so we can the proper section ids to make the subsequent queries with
  getInitialAssignmentQuery() {
    return resource('assignment_v3')
      .mandarkEndpoint(['assignments_v3', appStore.routerProps.params.examId])
      .include('sections_v1');
  },

  getAssignmentQuery() {
    const selectedSection = this.getSectionSelectorStore().getSelectedItem();
    return resource('assignment_v3')
      .mandarkEndpoint(['assignments_v3', appStore.routerProps.params.examId])
      .include('sections_v1.students_v2')
      .include('sections_v1.question_sets_v1.questions_v2')
      .include('sections_v1.assignment_v3')
      .include('question_sets_v1.questions_v2')
      .include('students_v2')
      .customQuery({
        with_meta: 'assignment_v3,student_v2,section_v1,question_v2'
      })
      .meta({
        context: {
          assignment: {
            id: appStore.routerProps.params.examId
          },
          section: {
            id: selectedSection ? selectedSection.getId() : this.getActiveSection().getId()
          },
          teacher: {
            id: masqueradeStore.getUserIdByMasqueradeState()
          }
        }
      });
  },

  isDataReady() {
    return (
      this.getInitialAssignmentQuery().isResourcePopulated() &&
      this.getAssignmentQuery().isResourcePopulated()
    );
  },

  getSectionSelectorStore() {
    return setUpStore(
      BlockyDropdownStore,
      `$activeAssignmentSectionSelectorStore/${appStore.routerProps.params.examId}`
    );
  },

  getSectionSelectorStoreName() {
    return this.getSectionSelectorStore().getName();
  },

  getActiveSection(assignment) {
    const currentSelection = this.getSectionSelectorStore().getSelectedItem();
    return assignment && currentSelection
      ? assignment.getSections().find((section) => section.getId() === currentSelection.getId())
      : this.getInitialAssignmentQuery()
          .getResource()
          .getSortedSections()
          .first();
  },

  generateStudentCSVData(section, assignment) {
    return [
      'Student Name',
      'Student Email',
      'Submission Status',
      'Total Questions Answered',
      'Questions Answered Incorrectly',
      'Questions Answered Correctly',
      'Grade',
      'Status'
    ]
      .join(',')
      .concat('\n')
      .concat(
        section.getStudents().reduce((acc, student) => {
          const correctGuessCount = student.getMeta().getSectionCountOfCorrectGuesses();
          const incorrectGuessCount = student.getMeta().getSectionCountOfIncorrectGuesses();
          const hasStarted =
            student
              .getSectionRelationships()
              .get(section.getId())
              .getStartTime() !== null;
          const hasFinished =
            student
              .getSectionRelationships()
              .get(section.getId())
              .getSubmittedAt() !== null;
          const accuracy = hasFinished
            ? stats.percentage(correctGuessCount, section.getMeta().getCountOfQuestions())
            : stats.percentage(correctGuessCount, incorrectGuessCount);
          return (
            acc +
            [
              student.getFullName(),
              student.getEmail(),
              getSubmissionStatus(student, section, assignment).display,
              correctGuessCount + incorrectGuessCount,
              incorrectGuessCount,
              correctGuessCount,
              accuracy,
              getStatusNameByAccuracy(accuracy, hasStarted)
            ]
              .join(',')
              .concat('\n')
          );
        }, '')
      );
  },

  generateQuestionCSVData(section) {
    return [
      'Question Name',
      'Average Duration (seconds)',
      'Student Answers',
      'Students Missing Answers',
      'Correct Students',
      'Incorrect Students'
    ]
      .join(',')
      .concat('\n')
      .concat(
        section.getAllQuestions().reduce((acc, question) => {
          const correctGuessCount = question.getSectionCountOfCorrectGuesses();
          const incorrectGuessCount = question.getSectionCountOfIncorrectGuesses();
          const totalAnswerCount = correctGuessCount + incorrectGuessCount;
          const studentsCount = section.getStudents().size;
          return (
            acc +
            [
              question.getTitle(),
              Math.floor(question.getSectionAverageTimeElapsed() / 1000),
              totalAnswerCount,
              studentsCount - totalAnswerCount,
              correctGuessCount,
              incorrectGuessCount
            ]
              .join(',')
              .concat('\n')
          );
        }, '')
      );
  }
};
